'use client';

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { YouTubeVideoListResponse } from '@/shared/types/google';
import { FolderItemsResponseType } from '@/shared/apis/folder/get-folder-items';
import type { ProjectScope } from '@/shared/types';

// ===================================
// 타입 정의
// ===================================

/** 소스 파일 타입 (Backend DTO와 매칭) */
export type SourceFileType = 'LOCAL_FILE' | 'URL_CONTENT' | 'EXISTING_CONTENT';

/** 콘텐츠 타입 */
export type ContentType = 'VIDEO' | 'IMAGE' | 'AUDIO';

export type ContentSourceRef = {
  type: 'DESKTOP_LOCAL' | 'RENDER_SERVICE_NAS';
  rootId: string;
  relativePath: string;
};

/** 소스 파일 상태 */
export type SourceFileStatus =
  | 'extracting' // 메타데이터 추출 중
  | 'ready' // 업로드 대기
  | 'uploading' // 업로드 중
  | 'completed' // 완료
  | 'error'; // 에러

/**
 * 통합 소스 파일 인터페이스
 * - LOCAL_FILE: 로컬 파일 업로드
 * - URL_CONTENT: YouTube 등 URL 기반 콘텐츠
 * - EXISTING_CONTENT: My Space 기존 콘텐츠
 */
export interface SourceFile {
  /** 고유 ID */
  id: string;
  /** 스코프 (프로젝트 타입별 파일 분리용) */
  scope?: ProjectScope;
  /** 소스 타입 */
  sourceType: SourceFileType;
  /** 콘텐츠 타입 (VIDEO, IMAGE, AUDIO) */
  contentType: ContentType;

  // === LOCAL_FILE 전용 ===
  /** 로컬 파일 객체 */
  localFile?: File;
  /** 미리보기용 Blob URL */
  blobUrl?: string;

  // === URL_CONTENT 전용 (YouTube 등) ===
  /** 원본 URL */
  url?: string;
  /** 썸네일 URL */
  thumbnailUrl?: string;
  /** YouTube API 응답 (메타데이터 보존용) */
  youtubeMetadata?: YouTubeVideoListResponse;

  // === EXISTING_CONTENT 전용 (My Space) ===
  /** 기존 콘텐츠 ID */
  contentId?: number;
  /** 기존 콘텐츠 메타데이터 */
  spaceMetadata?: FolderItemsResponseType;

  // === Desktop (Electron) 전용 ===
  /** 데스크톱: 프록시 파일 경로 (480p 변환본) */
  desktopProxyPath?: string;
  /** 데스크톱: 원본 파일 경로 (로컬 렌더링용) */
  desktopOriginalPath?: string;
  /** 데스크톱: 서버에 저장 가능한 root-relative 원본 참조 */
  desktopSourceRef?: ContentSourceRef;
  /** 데스크톱: 업로드 시 적용된 트림 범위 (원본 기준 초). 렌더링 시 오프셋 보정에 사용 */
  desktopTrimRange?: { startSec: number; endSec: number };
  /** 데스크톱: 소스 파일 분석 시 발견된 경고 메시지 (예: 비호환 오디오 코덱) */
  desktopWarnings?: string[];
  /** 렌더 서비스: NAS 원본 파일 참조 */
  renderServiceSource?: { rootId: string; relativePath: string };
  /** 렌더 서비스: 프록시 생성 job id */
  renderServiceProxyJobId?: string;
  /** 렌더 서비스: 프록시 스트리밍 URL */
  renderServiceProxyUrl?: string;

  // === 공통 메타데이터 ===
  /** 파일/콘텐츠 이름 */
  name: string;
  /** 파일 확장자 (예: mp4, jpg) */
  extension: string;
  /** 메타데이터 (duration, width, height, fileSize 등) */
  metadata?: {
    /** 파일 크기 (bytes) */
    fileSize?: number;
    /** 재생 시간 (ms) - VIDEO, AUDIO */
    duration?: number;
    /** 너비 (px) - VIDEO, IMAGE */
    width?: number;
    /** 높이 (px) - VIDEO, IMAGE */
    height?: number;
  };

  // === 상태 ===
  /** 현재 상태 */
  status: SourceFileStatus;
  /** 업로드 진행률 (0-100) */
  uploadProgress: number;
  /** 에러 메시지 */
  errorMessage?: string;
}

/**
 * 프로젝트 생성 소스 타입
 */
export type ProjectCreationSource = 'lts' | 'mts' | null;

// ===================================
// 스토어 상태 인터페이스
// ===================================

interface SourceFileState {
  // 상태
  files: SourceFile[];
  isProcessing: boolean; // 메타데이터 추출 중
  isUploading: boolean; // 업로드 중
  isCreatingProject: boolean; // 프로젝트 생성 중
  projectCreationSource: ProjectCreationSource;

  // 파일 관리 액션
  addFiles: (files: SourceFile[]) => void;
  removeFile: (id: string) => void;
  updateFile: (id: string, updates: Partial<SourceFile>) => void;
  updateProgress: (id: string, progress: number) => void;
  setStatus: (
    id: string,
    status: SourceFileStatus,
    errorMessage?: string,
  ) => void;

  // 중복 검사
  isDuplicate: (name: string, size?: number) => boolean;
  /** 스코프 기반 중복 검사 */
  isDuplicateByScope: (
    scope: ProjectScope,
    name: string,
    size?: number,
  ) => boolean;

  // 상태 관리
  setIsProcessing: (isProcessing: boolean) => void;
  setIsUploading: (isUploading: boolean) => void;
  startProjectCreation: (source: ProjectCreationSource) => void;
  endProjectCreation: () => void;

  // 헬퍼
  getTotalDuration: () => number;
  getFilesByType: (type: SourceFileType) => SourceFile[];
  /** 스코프별 파일 조회 */
  getFilesByScope: (scope: ProjectScope) => SourceFile[];
  /** 스코프별 총 재생 시간 (ms) */
  getTotalDurationByScope: (scope: ProjectScope) => number;

  // 리셋
  reset: () => void;
  /** 특정 스코프의 파일만 리셋 */
  resetByScope: (scope: ProjectScope) => void;
}

// ===================================
// 초기 상태
// ===================================

const initialState = {
  files: [] as SourceFile[],
  isProcessing: false,
  isUploading: false,
  isCreatingProject: false,
  projectCreationSource: null as ProjectCreationSource,
};

// ===================================
// 스토어 생성
// ===================================

export const useSourceFileStore = create<SourceFileState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      /**
       * 파일 추가
       */
      addFiles: (newFiles) =>
        set(
          (state) => ({
            files: [...state.files, ...newFiles],
          }),
          false,
          'addFiles',
        ),

      /**
       * 파일 제거 (Blob URL 정리 포함)
       */
      removeFile: (id) =>
        set(
          (state) => {
            const fileToRemove = state.files.find((f) => f.id === id);

            // Blob URL 정리
            if (fileToRemove?.blobUrl?.startsWith('blob:')) {
              try {
                URL.revokeObjectURL(fileToRemove.blobUrl);
              } catch (error) {
                console.warn('Failed to revoke blob URL:', error);
              }
            }

            return {
              files: state.files.filter((f) => f.id !== id),
            };
          },
          false,
          'removeFile',
        ),

      /**
       * 파일 업데이트 (트리밍 결과 반영 등)
       */
      updateFile: (id, updates) =>
        set(
          (state) => ({
            files: state.files.map((f) =>
              f.id === id ? { ...f, ...updates } : f,
            ),
          }),
          false,
          'updateFile',
        ),

      /**
       * 업로드 진행률 업데이트
       */
      updateProgress: (id, progress) =>
        set(
          (state) => ({
            files: state.files.map((f) =>
              f.id === id ? { ...f, uploadProgress: progress } : f,
            ),
          }),
          false,
          'updateProgress',
        ),

      /**
       * 파일 상태 업데이트
       */
      setStatus: (id, status, errorMessage) =>
        set(
          (state) => ({
            files: state.files.map((f) =>
              f.id === id ? { ...f, status, errorMessage } : f,
            ),
          }),
          false,
          'setStatus',
        ),

      /**
       * 중복 파일 검사 (전체 파일명 + 크기)
       * @param fullFileName - 전체 파일명 (확장자 포함, 예: "video.mp4")
       * @param size - 파일 크기 (bytes)
       */
      isDuplicate: (fullFileName, size) => {
        const { files } = get();
        return files.some((f) => {
          if (f.sourceType !== 'LOCAL_FILE') return false;

          // 전체 파일명 생성 (name + extension)
          const storedFullName = f.extension
            ? `${f.name}.${f.extension}`
            : f.name;

          return (
            storedFullName === fullFileName && f.metadata?.fileSize === size
          );
        });
      },

      /**
       * 스코프 기반 중복 파일 검사
       */
      isDuplicateByScope: (scope, fullFileName, size) => {
        const { files } = get();
        return files.some((f) => {
          if (f.sourceType !== 'LOCAL_FILE') return false;
          if (f.scope !== scope) return false;

          const storedFullName = f.extension
            ? `${f.name}.${f.extension}`
            : f.name;

          return (
            storedFullName === fullFileName && f.metadata?.fileSize === size
          );
        });
      },

      /**
       * 메타데이터 추출 중 상태 설정
       */
      setIsProcessing: (isProcessing) =>
        set({ isProcessing }, false, 'setIsProcessing'),

      /**
       * 업로드 중 상태 설정
       */
      setIsUploading: (isUploading) =>
        set({ isUploading }, false, 'setIsUploading'),

      /**
       * 프로젝트 생성 시작 (업로드 중 상태도 함께 설정)
       */
      startProjectCreation: (source) =>
        set(
          {
            isCreatingProject: true,
            isUploading: true,
            projectCreationSource: source,
          },
          false,
          'startProjectCreation',
        ),

      /**
       * 프로젝트 생성 완료 (업로드 중 상태도 함께 해제)
       */
      endProjectCreation: () =>
        set(
          {
            isCreatingProject: false,
            isUploading: false,
            projectCreationSource: null,
          },
          false,
          'endProjectCreation',
        ),

      /**
       * 총 재생 시간 계산 (ms)
       */
      getTotalDuration: () => {
        const { files } = get();
        return files.reduce(
          (total, file) => total + (file.metadata?.duration || 0),
          0,
        );
      },

      /**
       * 타입별 파일 조회
       */
      getFilesByType: (type) => {
        const { files } = get();
        return files.filter((f) => f.sourceType === type);
      },

      /**
       * 스코프별 파일 조회
       */
      getFilesByScope: (scope) => {
        const { files } = get();
        return files.filter((f) => f.scope === scope);
      },

      /**
       * 스코프별 총 재생 시간 (ms)
       */
      getTotalDurationByScope: (scope) => {
        const { files } = get();
        return files
          .filter((f) => f.scope === scope)
          .reduce((total, file) => total + (file.metadata?.duration || 0), 0);
      },

      /**
       * 상태 초기화 (Blob URL 정리 포함)
       */
      reset: () =>
        set(
          (state) => {
            // 모든 Blob URL 정리
            state.files.forEach((file) => {
              if (file.blobUrl?.startsWith('blob:')) {
                try {
                  URL.revokeObjectURL(file.blobUrl);
                } catch (error) {
                  console.warn(
                    'Failed to revoke blob URL during reset:',
                    error,
                  );
                }
              }
            });

            return initialState;
          },
          false,
          'reset',
        ),

      /**
       * 특정 스코프의 파일만 리셋 (Blob URL 정리 포함)
       * 해당 스코프가 프로젝트 생성 중이었다면 생성 상태도 함께 초기화
       */
      resetByScope: (scope) =>
        set(
          (state) => {
            // 해당 스코프 파일의 Blob URL 정리
            state.files.forEach((file) => {
              if (file.scope === scope && file.blobUrl?.startsWith('blob:')) {
                try {
                  URL.revokeObjectURL(file.blobUrl);
                } catch (error) {
                  console.warn(
                    'Failed to revoke blob URL during resetByScope:',
                    error,
                  );
                }
              }
            });

            // 해당 스코프가 프로젝트 생성 소스였으면 생성 상태도 초기화
            const shouldResetCreation = state.projectCreationSource === scope;

            return {
              files: state.files.filter((f) => f.scope !== scope),
              ...(shouldResetCreation && {
                isCreatingProject: false,
                isUploading: false,
                projectCreationSource: null,
              }),
            };
          },
          false,
          'resetByScope',
        ),
    }),
    {
      name: 'source-file-store',
    },
  ),
);

// ===================================
// 헬퍼 함수
// ===================================

/**
 * 고유 ID 생성
 */
export const generateSourceFileId = (name: string): string =>
  `${name}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * 파일 확장자에서 콘텐츠 타입 추론
 */
export const getContentTypeFromExtension = (extension: string): ContentType => {
  const ext = extension.toLowerCase();

  const videoExtensions = ['mp4', 'mov', 'webm', 'avi', 'mkv', 'm4v'];
  const audioExtensions = ['mp3', 'wav', 'aac', 'ogg', 'flac', 'm4a'];
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

  if (videoExtensions.includes(ext)) return 'VIDEO';
  if (audioExtensions.includes(ext)) return 'AUDIO';
  if (imageExtensions.includes(ext)) return 'IMAGE';

  // 기본값은 VIDEO
  return 'VIDEO';
};

/**
 * 파일명에서 확장자 추출
 */
export const getExtensionFromFileName = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : '';
};

/**
 * 파일명에서 이름 추출 (확장자 제외)
 */
export const getNameWithoutExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName;
};

// ===================================
// 하위 호환성을 위한 re-export
// ===================================

/** @deprecated useSourceFileStore를 사용하세요. */
export const useProjectSourceUploadStore = useSourceFileStore;
